import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Avatar,
  Alert,
  InputAdornment,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  Edit,
  Delete,
  NoteAdd,
  Search,
  Person,
  CalendarMonth,
  Phone,
  Email,
  Notes,
  CheckCircle,
  Schedule,
  PersonAdd,
  PersonOutline,
  Refresh,
  Today,
  EventNote,
  Close,
  Save,
  HistoryEdu,
  Lock,
  Visibility,
  VisibilityOff,
  Logout,
  MedicalServices,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const MotionCard = motion(Card);

// Booking interface
interface Booking {
  id: string;
  confirmationNumber: string;
  patientType: 'new' | 'existing';
  service: string;
  serviceDuration: number;
  date: string;
  time: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  patientNotes: string; // Notes from the patient
  staffNotes: Note[];
  status: 'upcoming' | 'completed' | 'cancelled' | 'no-show';
  createdAt: string;
}

// Note interface
interface Note {
  id: string;
  text: string;
  author: string;
  createdAt: string;
  updatedAt?: string;
}

// Sample bookings data for demo
const generateSampleBookings = (): Booking[] => {
  const services = [
    { name: 'Initial Consultation', duration: 60 },
    { name: 'Chiropractic Adjustment', duration: 30 },
    { name: 'Sports Therapy', duration: 45 },
    { name: 'Therapeutic Massage', duration: 45 },
    { name: 'Wellness Check', duration: 20 },
  ];

  const firstNames = ['Emma', 'Liam', 'Olivia', 'Noah', 'Ava', 'James', 'Sophia', 'Oliver', 'Isabella', 'William'];
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Wilson', 'Taylor'];

  const bookings: Booking[] = [];
  const today = new Date();

  for (let i = 0; i < 15; i++) {
    const service = services[Math.floor(Math.random() * services.length)];
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const dayOffset = Math.floor(Math.random() * 14) - 3; // -3 to +10 days
    const bookingDate = new Date(today);
    bookingDate.setDate(bookingDate.getDate() + dayOffset);

    const hours = 8 + Math.floor(Math.random() * 9);
    const minutes = Math.random() > 0.5 ? '00' : '30';

    const status: Booking['status'] = dayOffset < 0
      ? (Math.random() > 0.2 ? 'completed' : 'no-show')
      : (Math.random() > 0.95 ? 'cancelled' : 'upcoming');

    bookings.push({
      id: `booking-${i}`,
      confirmationNumber: `HBC-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      patientType: Math.random() > 0.4 ? 'existing' : 'new',
      service: service.name,
      serviceDuration: service.duration,
      date: bookingDate.toISOString().split('T')[0],
      time: `${hours.toString().padStart(2, '0')}:${minutes}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@email.com`,
      phone: `04${Math.floor(Math.random() * 100000000).toString().padStart(8, '0')}`,
      dateOfBirth: `${1960 + Math.floor(Math.random() * 50)}-${(1 + Math.floor(Math.random() * 12)).toString().padStart(2, '0')}-${(1 + Math.floor(Math.random() * 28)).toString().padStart(2, '0')}`,
      patientNotes: Math.random() > 0.7 ? 'Lower back pain for the past 2 weeks.' : '',
      staffNotes: [],
      status,
      createdAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
    });
  }

  return bookings.sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time}`);
    const dateB = new Date(`${b.date}T${b.time}`);
    return dateA.getTime() - dateB.getTime();
  });
};

// Demo staff accounts
const DEMO_STAFF = [
  { id: '1', name: 'Dr. Sarah Mitchell', role: 'Chiropractor', pin: '1234', avatar: 'SM' },
  { id: '2', name: 'Dr. James Chen', role: 'Chiropractor', pin: '5678', avatar: 'JC' },
  { id: '3', name: 'Emily Thompson', role: 'Reception', pin: '0000', avatar: 'ET' },
  { id: '4', name: 'Michael Roberts', role: 'Practice Manager', pin: '9999', avatar: 'MR' },
];

const BookingAdmin: React.FC = () => {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<typeof DEMO_STAFF[0] | null>(null);
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Existing state
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [staffName, setStaffName] = useState('Reception');
  const [activeTab, setActiveTab] = useState(0);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  // Check for existing session
  useEffect(() => {
    const savedSession = localStorage.getItem('hbc-staff-session');
    if (savedSession) {
      const staff = JSON.parse(savedSession);
      setSelectedStaff(staff);
      setStaffName(staff.name);
      setIsLoggedIn(true);
    }
  }, []);

  // Load bookings from localStorage or generate sample data
  useEffect(() => {
    if (!isLoggedIn) return;

    const savedBookings = localStorage.getItem('hbc-bookings');
    if (savedBookings) {
      setBookings(JSON.parse(savedBookings));
    } else {
      const sampleBookings = generateSampleBookings();
      setBookings(sampleBookings);
      localStorage.setItem('hbc-bookings', JSON.stringify(sampleBookings));
    }
  }, [isLoggedIn]);

  // Handle login
  const handleLogin = () => {
    if (!selectedStaff) {
      setLoginError('Please select a staff member');
      return;
    }
    if (pin !== selectedStaff.pin) {
      setLoginError('Incorrect PIN');
      setPin('');
      return;
    }

    setIsLoggingIn(true);
    setLoginError('');

    // Simulate login delay
    setTimeout(() => {
      setStaffName(selectedStaff.name);
      localStorage.setItem('hbc-staff-session', JSON.stringify(selectedStaff));
      setIsLoggedIn(true);
      setIsLoggingIn(false);
    }, 800);
  };

  // Handle logout
  const handleLogout = () => {
    setIsLoggedIn(false);
    setSelectedStaff(null);
    setPin('');
    localStorage.removeItem('hbc-staff-session');
  };

  // Save bookings to localStorage
  const saveBookings = (updatedBookings: Booking[]) => {
    setBookings(updatedBookings);
    localStorage.setItem('hbc-bookings', JSON.stringify(updatedBookings));
  };

  // Filter bookings based on tab and search
  const getFilteredBookings = () => {
    let filtered = bookings;

    // Filter by tab
    const today = new Date().toISOString().split('T')[0];
    switch (activeTab) {
      case 0: // Today
        filtered = filtered.filter(b => b.date === today);
        break;
      case 1: // Upcoming
        filtered = filtered.filter(b => b.date >= today && b.status === 'upcoming');
        break;
      case 2: // Past
        filtered = filtered.filter(b => b.date < today || b.status === 'completed');
        break;
      case 3: // All
        break;
    }

    // Filter by search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        b.firstName.toLowerCase().includes(search) ||
        b.lastName.toLowerCase().includes(search) ||
        b.email.toLowerCase().includes(search) ||
        b.phone.includes(search) ||
        b.confirmationNumber.toLowerCase().includes(search)
      );
    }

    return filtered;
  };

  // Add note to booking
  const handleAddNote = () => {
    if (!selectedBooking || !newNote.trim()) return;

    const note: Note = {
      id: `note-${Date.now()}`,
      text: newNote.trim(),
      author: staffName,
      createdAt: new Date().toISOString(),
    };

    const updatedBookings = bookings.map(b =>
      b.id === selectedBooking.id
        ? { ...b, staffNotes: [...b.staffNotes, note] }
        : b
    );

    saveBookings(updatedBookings);
    setSelectedBooking({
      ...selectedBooking,
      staffNotes: [...selectedBooking.staffNotes, note],
    });
    setNewNote('');
  };

  // Edit note
  const handleEditNote = (noteId: string) => {
    if (!selectedBooking || !editingNoteText.trim()) return;

    const updatedBookings = bookings.map(b =>
      b.id === selectedBooking.id
        ? {
            ...b,
            staffNotes: b.staffNotes.map(n =>
              n.id === noteId
                ? { ...n, text: editingNoteText.trim(), updatedAt: new Date().toISOString() }
                : n
            ),
          }
        : b
    );

    saveBookings(updatedBookings);
    setSelectedBooking({
      ...selectedBooking,
      staffNotes: selectedBooking.staffNotes.map(n =>
        n.id === noteId
          ? { ...n, text: editingNoteText.trim(), updatedAt: new Date().toISOString() }
          : n
      ),
    });
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  // Delete note
  const handleDeleteNote = (noteId: string) => {
    if (!selectedBooking) return;

    const updatedBookings = bookings.map(b =>
      b.id === selectedBooking.id
        ? { ...b, staffNotes: b.staffNotes.filter(n => n.id !== noteId) }
        : b
    );

    saveBookings(updatedBookings);
    setSelectedBooking({
      ...selectedBooking,
      staffNotes: selectedBooking.staffNotes.filter(n => n.id !== noteId),
    });
  };

  // Update booking status
  const handleStatusChange = (bookingId: string, status: Booking['status']) => {
    const updatedBookings = bookings.map(b =>
      b.id === bookingId ? { ...b, status } : b
    );
    saveBookings(updatedBookings);
    if (selectedBooking?.id === bookingId) {
      setSelectedBooking({ ...selectedBooking, status });
    }
  };

  // Get status color
  const getStatusColor = (status: Booking['status']) => {
    switch (status) {
      case 'upcoming':
        return { bg: '#e3f2fd', color: '#1976d2' };
      case 'completed':
        return { bg: '#e8f5e9', color: '#388e3c' };
      case 'cancelled':
        return { bg: '#ffebee', color: '#d32f2f' };
      case 'no-show':
        return { bg: '#fff3e0', color: '#f57c00' };
      default:
        return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  // Get counts for tabs
  const today = new Date().toISOString().split('T')[0];
  const todayCount = bookings.filter(b => b.date === today).length;
  const upcomingCount = bookings.filter(b => b.date >= today && b.status === 'upcoming').length;

  const filteredBookings = getFilteredBookings();

  // Login Screen
  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Container maxWidth="sm">
          <Fade in timeout={600}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
            >
              {/* Logo/Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #8bc34a 0%, #689f38 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 8px 24px rgba(139, 195, 74, 0.3)',
                  }}
                >
                  <MedicalServices sx={{ fontSize: 36, color: 'white' }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 0.5 }}>
                  Staff Portal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Health by Chiropractic
                </Typography>
              </Box>

              {/* Staff Selection */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
                Select Staff Member
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {DEMO_STAFF.map((staff) => (
                  <Grid item xs={6} key={staff.id}>
                    <Paper
                      elevation={0}
                      onClick={() => {
                        setSelectedStaff(staff);
                        setLoginError('');
                        setPin('');
                      }}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: selectedStaff?.id === staff.id
                          ? '2px solid #8bc34a'
                          : '2px solid #e0e0e0',
                        backgroundColor: selectedStaff?.id === staff.id
                          ? 'rgba(139, 195, 74, 0.08)'
                          : 'white',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#8bc34a',
                          backgroundColor: 'rgba(139, 195, 74, 0.05)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            backgroundColor: selectedStaff?.id === staff.id ? '#8bc34a' : '#e0e0e0',
                          }}
                        >
                          {staff.avatar}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {staff.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {staff.role}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* PIN Entry */}
              {selectedStaff && (
                <Fade in>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#2c3e50' }}>
                      Enter PIN for {selectedStaff.name.split(' ')[0]}
                    </Typography>
                    <TextField
                      fullWidth
                      type={showPin ? 'text' : 'password'}
                      placeholder="Enter 4-digit PIN"
                      value={pin}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setPin(val);
                        setLoginError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && pin.length === 4) {
                          handleLogin();
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPin(!showPin)} edge="end">
                              {showPin ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '1.25rem',
                          letterSpacing: showPin ? 'normal' : '0.5em',
                          textAlign: 'center',
                        },
                      }}
                    />

                    {loginError && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {loginError}
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleLogin}
                      disabled={pin.length !== 4 || isLoggingIn}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: '#8bc34a',
                        '&:hover': { backgroundColor: '#7cb342' },
                        '&:disabled': { backgroundColor: '#e0e0e0' },
                      }}
                    >
                      {isLoggingIn ? (
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </Box>
                </Fade>
              )}

              {/* Demo Hint */}
              <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                <Typography variant="caption">
                  <strong>Demo Mode:</strong> Select any staff member and use their PIN to login.
                  <br />
                  Reception PIN: <strong>0000</strong> | Dr. Sarah PIN: <strong>1234</strong>
                </Typography>
              </Alert>
            </Paper>
          </Fade>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 1,
                background: 'linear-gradient(135deg, #2c3e50 0%, #8bc34a 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Booking Management
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Welcome back, {selectedStaff?.name}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{
              borderColor: '#e0e0e0',
              color: 'text.secondary',
              '&:hover': { borderColor: '#bdbdbd', backgroundColor: 'rgba(0,0,0,0.02)' },
            }}
          >
            Sign Out
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { label: "Today's Bookings", value: todayCount, icon: <Today />, color: '#2c3e50' },
            { label: 'Upcoming', value: upcomingCount, icon: <EventNote />, color: '#8bc34a' },
            { label: 'New Patients Today', value: bookings.filter(b => b.date === today && b.patientType === 'new').length, icon: <PersonAdd />, color: '#3498db' },
            { label: 'Completed Today', value: bookings.filter(b => b.date === today && b.status === 'completed').length, icon: <CheckCircle />, color: '#27ae60' },
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={stat.label}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: stat.color }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                    </Box>
                    <Avatar sx={{ backgroundColor: `${stat.color}15`, color: stat.color, width: 56, height: 56 }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>

        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          {/* Toolbar */}
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by name, email, phone, or confirmation #"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Staff Name"
                  value={staffName}
                  onChange={(e) => {
                    setStaffName(e.target.value);
                    localStorage.setItem('hbc-staff-name', e.target.value);
                  }}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => {
                    const sampleBookings = generateSampleBookings();
                    saveBookings(sampleBookings);
                  }}
                  sx={{ borderRadius: 2, height: 40 }}
                >
                  Reset Demo
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              px: 3,
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              '& .MuiTab-root': { fontWeight: 600 },
            }}
          >
            <Tab
              label={
                <Badge badgeContent={todayCount} color="primary" sx={{ '& .MuiBadge-badge': { right: -12 } }}>
                  Today
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={upcomingCount} color="success" sx={{ '& .MuiBadge-badge': { right: -12 } }}>
                  Upcoming
                </Badge>
              }
            />
            <Tab label="Past" />
            <Tab label="All Bookings" />
          </Tabs>

          {/* Table */}
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8f9fa' }}>Patient</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8f9fa' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8f9fa' }}>Service</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8f9fa' }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8f9fa' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8f9fa' }}>Notes</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8f9fa' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {filteredBookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8 }}>
                        <Typography color="text.secondary">No bookings found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBookings.map((booking, index) => (
                      <TableRow
                        key={booking.id}
                        component={motion.tr}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.03 }}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(139, 195, 74, 0.05)' },
                        }}
                        onClick={() => {
                          setSelectedBooking(booking);
                          setNoteDialogOpen(true);
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ backgroundColor: booking.patientType === 'new' ? '#2c3e50' : '#8bc34a' }}>
                              {booking.firstName[0]}{booking.lastName[0]}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>
                                {booking.firstName} {booking.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.confirmationNumber}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={booking.patientType === 'new' ? <PersonAdd sx={{ fontSize: 16 }} /> : <PersonOutline sx={{ fontSize: 16 }} />}
                            label={booking.patientType === 'new' ? 'New' : 'Existing'}
                            sx={{
                              backgroundColor: booking.patientType === 'new' ? '#2c3e5015' : '#8bc34a15',
                              color: booking.patientType === 'new' ? '#2c3e50' : '#689f38',
                              fontWeight: 600,
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{booking.service}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.serviceDuration} min
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDate(booking.date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.time}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1).replace('-', ' ')}
                            sx={{
                              backgroundColor: getStatusColor(booking.status).bg,
                              color: getStatusColor(booking.status).color,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {booking.staffNotes.length > 0 ? (
                            <Tooltip title={`${booking.staffNotes.length} note(s)`}>
                              <Badge badgeContent={booking.staffNotes.length} color="primary">
                                <Notes sx={{ color: '#8bc34a' }} />
                              </Badge>
                            </Tooltip>
                          ) : (
                            <Notes sx={{ color: '#bdc3c7' }} />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Add/View Notes">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBooking(booking);
                                setNoteDialogOpen(true);
                              }}
                            >
                              <NoteAdd />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Booking Detail Dialog */}
        <Dialog
          open={noteDialogOpen}
          onClose={() => {
            setNoteDialogOpen(false);
            setEditingNoteId(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4 },
          }}
        >
          {selectedBooking && (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {selectedBooking.firstName} {selectedBooking.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedBooking.confirmationNumber}
                    </Typography>
                  </Box>
                  <IconButton onClick={() => setNoteDialogOpen(false)}>
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={3}>
                  {/* Patient Info */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, backgroundColor: '#f8f9fa' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person sx={{ color: '#8bc34a' }} /> Patient Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={selectedBooking.patientType === 'new' ? 'New Patient' : 'Existing Patient'}
                            sx={{
                              backgroundColor: selectedBooking.patientType === 'new' ? '#2c3e50' : '#8bc34a',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2">{selectedBooking.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2">{selectedBooking.phone}</Typography>
                        </Box>
                        {selectedBooking.dateOfBirth && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarMonth sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2">DOB: {selectedBooking.dateOfBirth}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Appointment Info */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, backgroundColor: '#f8f9fa' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ color: '#8bc34a' }} /> Appointment
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2">
                          <strong>Service:</strong> {selectedBooking.service} ({selectedBooking.serviceDuration} min)
                        </Typography>
                        <Typography variant="body2">
                          <strong>Date:</strong> {formatDate(selectedBooking.date)} at {selectedBooking.time}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <strong>Status:</strong>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {(['upcoming', 'completed', 'cancelled', 'no-show'] as const).map((status) => (
                              <Chip
                                key={status}
                                size="small"
                                label={status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                                onClick={() => handleStatusChange(selectedBooking.id, status)}
                                sx={{
                                  backgroundColor: selectedBooking.status === status
                                    ? getStatusColor(status).color
                                    : getStatusColor(status).bg,
                                  color: selectedBooking.status === status
                                    ? 'white'
                                    : getStatusColor(status).color,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: getStatusColor(status).color,
                                    color: 'white',
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Patient Notes */}
                  {selectedBooking.patientNotes && (
                    <Grid item xs={12}>
                      <Alert severity="info" icon={<Notes />} sx={{ borderRadius: 2 }}>
                        <Typography variant="body2">
                          <strong>Patient's Notes:</strong> {selectedBooking.patientNotes}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}

                  {/* Staff Notes */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryEdu sx={{ color: '#8bc34a' }} /> Staff Notes
                    </Typography>

                    {selectedBooking.staffNotes.length === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                        No staff notes yet. Add a note below.
                      </Alert>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        {selectedBooking.staffNotes.map((note) => (
                          <Fade in key={note.id}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: '#f8f9fa',
                                border: '1px solid rgba(0,0,0,0.08)',
                              }}
                            >
                              {editingNoteId === note.id ? (
                                <Box>
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={editingNoteText}
                                    onChange={(e) => setEditingNoteText(e.target.value)}
                                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={<Save />}
                                      onClick={() => handleEditNote(note.id)}
                                      sx={{ backgroundColor: '#8bc34a' }}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setEditingNoteId(null);
                                        setEditingNoteText('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    {note.text}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {note.author} • {new Date(note.createdAt).toLocaleString('en-AU')}
                                      {note.updatedAt && ' (edited)'}
                                    </Typography>
                                    <Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setEditingNoteId(note.id);
                                          setEditingNoteText(note.text);
                                        }}
                                      >
                                        <Edit sx={{ fontSize: 16 }} />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteNote(note.id)}
                                        sx={{ color: '#e74c3c' }}
                                      >
                                        <Delete sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </>
                              )}
                            </Paper>
                          </Fade>
                        ))}
                      </Box>
                    )}

                    {/* Add Note Form */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Add a staff note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        sx={{
                          minWidth: 100,
                          background: 'linear-gradient(135deg, #8bc34a 0%, #689f38 100%)',
                          alignSelf: 'flex-end',
                        }}
                      >
                        Add Note
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setNoteDialogOpen(false)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default BookingAdmin;
